import React, { useMemo, useState } from 'react';
import { Badge, Col, Container, Row } from 'reactstrap';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import Fuse from 'fuse.js';
import useGetTopQuestionsCategories from '../../api/useGetTopQuestionsCategories';
import PopularArticles from './PopularArticles';
import { createUrlFromQuestion } from '../knowledge/createUrlFromQuestion';
import DisplayQueryError from './DisplayQueryError';
import LoadingSpinner from './LoadingSpinner';
import { Question, Tag } from '../../types';
import { FeaturedImage } from '../pages/TagViewPage';

const BrowseQuestion: React.FC = () => {
    const getTopQuestionsCategories = useGetTopQuestionsCategories();

    const {
        data: topQuestionsCategories,
        isLoading,
        error,
        refetch
    } = useQuery('browseQuestions', getTopQuestionsCategories);

    if (error) {
        return <DisplayQueryError error={error} refetch={refetch} />;
    }

    if (isLoading || !topQuestionsCategories) {
        return <LoadingSpinner />;
    }

    const { tags, questions } = topQuestionsCategories;

    const rootTags = tags.filter((t) => (t.parentTagId ?? '') === '');

    return (
        <Container className='pb-5'>
            <Row>
                <Col xs='12'>
                    <FeaturedTags tags={rootTags} />
                    <PopularQuestionsSection questions={questions} />
                    <PopularArticles />
                    <TagsSection tags={tags} />
                </Col>
            </Row>
        </Container>
    );
};

const FeaturedTags: React.FC<{
    tags: Tag[];
}> = ({ tags }) => {
    if (!tags || tags.length === 0) {
        return null;
    }

    return (
        <div>
            <Row className='d-flex align-items-center justify-content-center'>
                {tags
                    .filter((t) => t.featured)
                    .toSorted((a, b) => {
                        // Sort by ordinal OR alphabetically
                        if (a.ordinal != null && b.ordinal != null) {
                            return a.ordinal - b.ordinal;
                        }
                        return a.text.localeCompare(b.text);
                    })
                    .map((tag) => {
                        const hasArticles = (tag.articleCount ?? 0) > 0;
                        const hasQuestions = (tag.questionCount ?? 0) > 0;
                        const hasSubTags = (tag.subTagCount ?? 0) > 0;
                        if (!(hasArticles || hasQuestions || hasSubTags)) {
                            return null;
                        }
                        return (
                            <Col
                                xs='12'
                                sm='6'
                                md='6'
                                lg='4'
                                xl='3'
                                className='mb-4 d-flex justify-content-center'
                                key={tag.id}
                            >
                                <Link
                                    to={`/topic/${encodeURIComponent(
                                        tag.text
                                    )}`}
                                    className='featured-tag-card text-decoration-none'
                                >
                                    <div className='card shadow-sm border-0 rounded-3 overflow-hidden'>
                                        <div className='card-body featured-tag-card-body p-3 d-flex flex-column h-100'>
                                            <div className='d-flex align-items-center justify-content-center mb-3 featured-image-container'>
                                                <FeaturedImage tag={tag} />
                                            </div>
                                            <h5 className='card-title text-center mb-3 fw-semibold text-dark'>
                                                {tag.text}
                                            </h5>
                                            <p
                                                className='tag-description card-text text-center mb-3 text-muted flex-grow-1'
                                                style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {tag.description}
                                            </p>
                                            <div
                                                className='badges'
                                                style={{
                                                    marginTop: 'auto',
                                                    display: 'flex',
                                                    gap: '0.1rem',
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                {hasSubTags && (
                                                    <div className='mt-auto'>
                                                        <Badge
                                                            pill
                                                            className='subtopics-badge'
                                                            style={{
                                                                color: 'white',
                                                                backgroundColor:
                                                                    'var(--primary)'
                                                            }}
                                                        >
                                                            {tag.subTagCount ||
                                                                0}{' '}
                                                            sub-topics
                                                        </Badge>
                                                    </div>
                                                )}
                                                {hasArticles && (
                                                    <div className='mt-auto'>
                                                        <Badge
                                                            pill
                                                            className='articles-badge'
                                                            style={{
                                                                color: 'white',
                                                                backgroundColor:
                                                                    'var(--secondary)'
                                                            }}
                                                        >
                                                            {tag.articleCount ||
                                                                0}{' '}
                                                            articles
                                                        </Badge>
                                                    </div>
                                                )}
                                                {hasQuestions && (
                                                    <div className='mt-auto'>
                                                        <Badge
                                                            pill
                                                            className='questions-badge'
                                                            style={{
                                                                color: 'white',
                                                                backgroundColor:
                                                                    'var(--tertiary)'
                                                            }}
                                                        >
                                                            {tag.questionCount ||
                                                                0}{' '}
                                                            questions
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </Col>
                        );
                    })}
            </Row>
        </div>
    );
};

const PopularQuestionsSection: React.FC<{
    questions: Question[];
}> = ({ questions }) => {
    return (
        <div className='custom-card mt-4'>
            <div className='custom-card-head'>
                <div className='custom-card-heading'>
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 512 512'
                        className='custom-card-icon'
                    >
                        <defs>
                            <linearGradient
                                id='linear-gradient'
                                y1='-0.14'
                                x2='1.037'
                                y2='1.226'
                                gradientUnits='objectBoundingBox'
                            >
                                <stop offset='0' stopColor='var(--primary)' />
                                <stop
                                    offset='1'
                                    stopColor='var(--primary-light)'
                                />
                            </linearGradient>
                        </defs>

                        <path
                            fill='url(#linear-gradient)'
                            d='M256 448c141.4 0 256-93.1 256-208S397.4 32 256 32S0 125.1 0 240c0 45.1 17.7 86.8 47.7 120.9c-1.9 24.5-11.4 46.3-21.4 62.9c-5.5 9.2-11.1 16.6-15.2 21.6c-2.1 2.5-3.7 4.4-4.9 5.7c-.6 .6-1 1.1-1.3 1.4l-.3 .3 0 0 0 0 0 0 0 0c-4.6 4.6-5.9 11.4-3.4 17.4c2.5 6 8.3 9.9 14.8 9.9c28.7 0 57.6-8.9 81.6-19.3c22.9-10 42.4-21.9 54.3-30.6c31.8 11.5 67 17.9 104.1 17.9zM128 272c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32zm128 0c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32zm160-32c0 17.7-14.3 32-32 32s-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32z'
                        />
                    </svg>
                    <h3 className='mb-0'>Popular questions</h3>
                </div>
            </div>
            <div className='custom-card-body mt-3'>
                <Row>
                    {questions.map((question) => (
                        <Col
                            xs='12'
                            lg='6'
                            className='question-link-holder'
                            key={question.questionId}
                        >
                            <Link
                                to={createUrlFromQuestion(
                                    question.questionId,
                                    question.text
                                )}
                                className='question-link'
                            >
                                {question.text.charAt(0).toUpperCase() +
                                    question.text.slice(1)}
                            </Link>
                        </Col>
                    ))}
                </Row>
            </div>
        </div>
    );
};

const TagsSection: React.FC<{
    tags: Tag[];
}> = ({ tags }) => {
    const [query, setQuery] = useState('');

    const sortedTags = tags.toSorted((a, b) =>
        a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
    );
    const fuse = useMemo(
        () =>
            new Fuse(sortedTags, {
                keys: ['text', 'description'],
                threshold: 0.35,
                ignoreLocation: true,
                minMatchCharLength: 2
            }),
        [sortedTags]
    );

    const filteredTags = useMemo(() => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery || trimmedQuery.length < 2) {
            return sortedTags;
        }
        return fuse.search(trimmedQuery).map((result) => result.item);
    }, [fuse, query, sortedTags]);

    if (sortedTags.length == 0) {
        return null;
    }

    return (
        <div className='custom-card mt-4'>
            <div className='custom-card-head'>
                <div className='custom-card-heading'>
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 512 512'
                        className='custom-card-icon'
                    >
                        <defs>
                            <linearGradient
                                id='linear-gradient'
                                x1='0.077'
                                y1='1.363'
                                x2='1.183'
                                y2='0.148'
                                gradientUnits='objectBoundingBox'
                            >
                                <stop offset='0' stopColor='var(--primary)' />
                                <stop
                                    offset='1'
                                    stopColor='var(--primary-light)'
                                />
                            </linearGradient>
                        </defs>

                        <path
                            fill='url(#linear-gradient)'
                            d='M345 39.1L472.8 168.4c52.4 53 52.4 138.2 0 191.2L360.8 472.9c-9.3 9.4-24.5 9.5-33.9.2s-9.5-24.5-.2-33.9L438.6 325.9c33.9-34.3 33.9-89.4 0-123.7L310.9 72.9c-9.3-9.4-9.2-24.6.2-33.9s24.6-9.2 33.9 .2zM0 229.5V80C0 53.5 21.5 32 48 32H197.5c17 0 33.3 6.7 45.3 18.7l168 168c25 25 25 65.5 0 90.5L277.3 442.7c-25 25-65.5 25-90.5 0l-168-168C6.7 262.7 0 246.5 0 229.5zM144 144c0-17.7-14.3-32-32-32s-32 14.3-32 32s14.3 32 32 32s32-14.3 32-32z'
                        />
                    </svg>
                    <h3 className='mb-0'>Browse all topics</h3>
                </div>
            </div>
            <div className='custom-card-body mt-3'>
                <div className='mb-3'>
                    <input
                        className='form-control rounded-pill'
                        type='search'
                        placeholder='Search topics...'
                        aria-label='Search topics'
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                </div>
                <div className='pill-grid'>
                    {filteredTags.map((tag) => (
                        <Link
                            to={`/topic/${encodeURIComponent(tag.text)}`}
                            className='btn btn-outline-primary rounded-pill m-1'
                            key={tag.id}
                        >
                            {tag.text}
                        </Link>
                    ))}
                </div>
                {query.trim().length >= 2 && filteredTags.length === 0 && (
                    <div className='text-muted mt-3'>
                        No topics match your search.
                    </div>
                )}
            </div>
        </div>
    );
};

export default BrowseQuestion;
